import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { LogoutSaveService } from './logout-save.service';

export type LogoutConfirmationVariant = 'general' | 'program-view' | 'program-edit';

@Injectable({
  providedIn: 'root',
})
export class StaffLogoutService {
  readonly showConfirmationModal = signal(false);
  readonly logoutConfirmationVariant = signal<LogoutConfirmationVariant>('general');

  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly logoutSaveService = inject(LogoutSaveService);
  private isLoggingOut = false;

  requestLogout(): void {
    if (this.isLoggingOut) {
      return;
    }

    if (this.shouldShowLogoutConfirmation()) {
      this.logoutConfirmationVariant.set(this.getLogoutConfirmationVariant());
      this.showConfirmationModal.set(true);
      return;
    }

    this.performLogout();
  }

  confirmLogout(): void {
    if (this.isLoggingOut) {
      return;
    }

    this.showConfirmationModal.set(false);
    this.performLogout();
  }

  confirmSaveAndExit(): void {
    if (this.isLoggingOut) {
      return;
    }

    this.showConfirmationModal.set(false);

    if (this.router.url.startsWith('/program/')) {
      this.logoutSaveService.triggerSaveAndExit();
      setTimeout(() => {
        this.performLogout();
      }, 1000);
      return;
    }

    this.performLogout();
  }

  cancelLogout(): void {
    this.showConfirmationModal.set(false);
  }

  private shouldShowLogoutConfirmation(): boolean {
    const url = this.router.url.split('?')[0];
    if (url === '' || url === '/') {
      return false;
    }
    return !!sessionStorage.getItem('token');
  }

  private getLogoutConfirmationVariant(): LogoutConfirmationVariant {
    if (!this.router.url.startsWith('/program/')) {
      return 'general';
    }
    if (this.getIsProgramViewMode()) {
      return 'program-view';
    }
    return 'program-edit';
  }

  private getIsProgramViewMode(): boolean {
    const url = this.router.url;
    if (!url.startsWith('/program/')) {
      return false;
    }

    const queryParams = this.router.parseUrl(url).queryParams;
    return (
      queryParams['view'] === 'true' ||
      queryParams['reupload'] === 'true' ||
      queryParams['reuploadView'] === 'true'
    );
  }

  private performLogout(): void {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    this.authService.logout();

    setTimeout(() => {
      this.isLoggingOut = false;
    }, 1000);
  }
}
